import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
  useParams,
} from "react-router-dom";
import AdminLogin from "./components/Auth/AdminLogin";
import LeadLogin from "./components/Auth/LeadLogin";
import Signup from "./components/Auth/Signup";
// New email authentication components
import EmailSignup from "./components/Auth/EmailSignup";
import EmailLogin from "./components/Auth/EmailLogin";
import EmailVerification from "./components/Auth/EmailVerification";
import PhoneVerification from "./components/Auth/PhoneVerification";

import VerificationStatus from "./components/Auth/VerificationStatus";
import AudioAgent from "./components/VoiceAgent/AudioAgent";
import Leads from "./components/Admin/Leads";
import AgentConfiguration from "./components/Admin/AgentConfiguration";
import AgentEdit from "./components/Admin/AgentEdit";
import AgentCreate from "./components/Admin/AgentCreate";
import TagsManagement from "./components/Admin/TagsManagement";
import LanguageConfiguration from "./components/Admin/LanguageConfiguration";
import PasswordChange from "./components/Admin/PasswordChange";
import CampaignList from "./components/Admin/CampaignList";
import CampaignDetails from "./components/Admin/CampaignDetails";
import LeadCampaigns from "./components/Conversations/LeadCampaigns";
import Conversations from "./components/Conversations/Conversations";
import UnknownConversations from "./components/Admin/UnknownConversations";
import ThemeToggle from "./components/Common/ThemeToggle";
import LoadingState from "./components/Common/LoadingState";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { isAuthenticated, logout } from "./services/auth";
import { getVerificationStatus } from "./services/auth";
import { ToastProvider } from "./components/Common/Toast";
import "./App.css";
import { HiMenu, HiX, HiChevronDown, HiLogout } from "react-icons/hi";
import AgentSelector from "./components/VoiceAgent/AgentSelector";
import AgentFileUpload from "./components/VoiceAgent/AgentFileUpload";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaLinkedin,
  FaYoutube,
} from "react-icons/fa";
import { MdErrorOutline } from "react-icons/md";
import { Modal } from "./components/Common/Modal";
import { validateAgentToken } from "./services/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes (replaces cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Footer Component
export const Footer: React.FC = () => {
  const { theme } = useTheme();

  return (
    <footer className="bg-[color:var(--background)] mt-24 pb-6">
      <div className="container mx-auto px-6">
        {/* Main footer content */}
        <div className="flex flex-col sm:flex-row">
          {/* Responsive layout for both mobile and desktop */}
          <div className="flex flex-col sm:flex-row w-full">
            {/* QUICK LINKS section */}
            <div className="w-full sm:w-1/2 md:w-1/4 lg:w-1/6 mb-6 sm:mb-0">
              <h3 className="text-[color:var(--accent)] font-medium text-lg mb-4">
                QUICK LINKS
              </h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="https://ukti.ai/contact-us"
                    className="text-[color:var(--foreground)] hover:text-[color:var(--accent)] transition-colors"
                  >
                    Contact Us
                  </a>
                </li>
                <li>
                  <a
                    href="https://ukti.ai/pricing"
                    className="text-[color:var(--foreground)] hover:text-[color:var(--accent)] transition-colors"
                  >
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            {/* COMPLIANCE section */}
            <div className="w-full sm:w-1/2 md:w-1/4 lg:w-1/6 sm:pl-8 md:pl-12 lg:pl-16">
              <h3 className="text-[color:var(--accent)] font-medium text-lg mb-4">
                COMPLIANCE
              </h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="https://ukti.ai/privacy-policy"
                    className="text-[color:var(--foreground)] hover:text-[color:var(--accent)] transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="https://ukti.ai/terms-of-use"
                    className="text-[color:var(--foreground)] hover:text-[color:var(--accent)] transition-colors"
                  >
                    Terms of Use
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Logo and social icons */}
        <div className="mt-8 flex flex-col md:flex-row justify-between items-center">
          <div>
            <a
              href="https://ukti.ai"
              className="block"
            >
              {/* Logo changes based on theme from context */}
              <img
                src={
                  theme === "light"
                    ? "/assets/ukti-logo-with-text-dark.png"
                    : "/assets/ukti-logo-with-text-white.png"
                }
                alt="Ukti.ai"
                className="h-20 w-auto"
              />
            </a>
          </div>
        </div>

        {/* Copyright text */}
        <div className="mt-6 text-right text-[color:var(--foreground-muted)] text-sm">
          All Rights Reserved. 2025. ©
        </div>
      </div>
    </footer>
  );
};

// ScrollToTop component to handle automatic scrolling on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// NotFound Component
const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const isUserLoggedIn = isAuthenticated();

  // Redirect to signup if not logged in
  useEffect(() => {
    if (!isUserLoggedIn) {
              navigate("/signup", { replace: true });
    }
  }, [isUserLoggedIn, navigate]);

  // If not logged in, don't render anything while redirecting
  if (!isUserLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Redirecting..." fullPage={true} size="large" />
      </div>
    );
  }

  const handleNavigateHome = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <MdErrorOutline className="text-[color:var(--accent)] text-9xl mx-auto" />
      </motion.div>

      <motion.h1
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="text-4xl md:text-5xl font-bold mb-4 text-[color:var(--foreground)]"
      >
        Page Not Found
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="text-lg mb-8 text-[color:var(--foreground-muted)] max-w-md"
      >
        The page you are looking for doesn't exist or has been moved.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 0.3 }}
      >
        <button
          onClick={handleNavigateHome}
          className="px-6 py-3 bg-[color:var(--accent)] text-white rounded-md hover:bg-[color:var(--accent)]/90 transition-all duration-300"
        >
          Go to Dashboard
        </button>
      </motion.div>
    </div>
  );
};

// Enhanced authentication status checking with verification requirements
const useAuthenticationStatus = () => {
  const [authStatus, setAuthStatus] = useState({
    isLoggedIn: isAuthenticated(),
    emailVerified: false,
    phoneVerified: false,
    canAccessDashboard: false,
    loading: true,
  });

  const [lastStatusCheck, setLastStatusCheck] = useState<number>(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false);

  const checkAuthAndVerificationStatus = useCallback(async (forceRefresh = false) => {
    const isLoggedIn = isAuthenticated();
    
    if (!isLoggedIn) {
      setAuthStatus({
        isLoggedIn: false,
        emailVerified: false,
        phoneVerified: false,
        canAccessDashboard: false,
        loading: false,
      });
      return;
    }

    // Debounce verification status checks (only check every 5 seconds unless forced)
    const now = Date.now();
    if (!forceRefresh && isCheckingStatus) {
      console.log("Verification status check already in progress, skipping");
      return;
    }
    
    if (!forceRefresh && (now - lastStatusCheck) < 5000) {
      console.log("Recent verification status check, skipping");
      return;
    }

    // If logged in, check verification status
    try {
      setIsCheckingStatus(true);
      const email = localStorage.getItem("email");
      const phone = localStorage.getItem("phone");
      
      if (email || phone) {
        // Keep loading state while checking verification
        setAuthStatus(prev => ({ ...prev, loading: true }));
        
        const verificationResult = await getVerificationStatus(
          email || undefined, 
          phone || undefined
        );
        
        setLastStatusCheck(now);
        
        if (verificationResult.success && verificationResult.data) {
          setAuthStatus({
            isLoggedIn: true,
            emailVerified: verificationResult.data.email_verified,
            phoneVerified: verificationResult.data.phone_verified,
            canAccessDashboard: verificationResult.data.can_access_dashboard,
            loading: false,
          });
        } else {
          // If we can't get verification status but user is logged in,
          // assume basic authentication but don't show errors
          console.warn("Could not fetch verification status, assuming basic auth");
          setAuthStatus({
            isLoggedIn: true,
            emailVerified: true, // For existing users
            phoneVerified: true, // For existing users
            canAccessDashboard: true,
            loading: false,
          });
        }
      } else {
        // No email/phone stored, assume existing user (legacy flow)
        setAuthStatus({
          isLoggedIn: true,
          emailVerified: true,
          phoneVerified: true,
          canAccessDashboard: true,
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      // On error, assume basic authentication for existing users to prevent flash errors
      setAuthStatus({
        isLoggedIn: true,
        emailVerified: true,
        phoneVerified: true,
        canAccessDashboard: true,
        loading: false,
      });
    } finally {
      setIsCheckingStatus(false);
    }
  }, [lastStatusCheck, isCheckingStatus]);

  useEffect(() => {
    checkAuthAndVerificationStatus();
  }, []); // Only run once on mount

  return { ...authStatus, refreshAuthStatus: (force = false) => checkAuthAndVerificationStatus(force) };
};

// Enhanced Protected route component with verification requirements
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedUserType?: string;
  requireEmailVerification?: boolean;
  requirePhoneVerification?: boolean;
  requireFullVerification?: boolean;
}> = ({ 
  children, 
  allowedUserType, 
  requireEmailVerification = false,
  requirePhoneVerification = false,
  requireFullVerification = false 
}) => {
  const authStatus = useAuthenticationStatus();
  const userType = localStorage.getItem("userType");

  // Show loading while checking auth status
  if (authStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Verifying access..." fullPage={true} size="large" />
      </div>
    );
  }

  // Check basic authentication
  if (!authStatus.isLoggedIn) {
    // Store the current URL (including query parameters) for redirect after login
    // Only store if not already stored and not a login/auth page
    const currentUrl = window.location.pathname + window.location.search;
    const existingRedirectUrl = localStorage.getItem('redirectAfterLogin');
    if (!existingRedirectUrl && !currentUrl.includes('/login') && !currentUrl.includes('/signup')) {
      localStorage.setItem('redirectAfterLogin', currentUrl);
    }
    return <Navigate to="/login" replace />;
  }

  // Check user type restrictions
  if (allowedUserType && userType !== allowedUserType) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check verification requirements for non-admin users
  if (userType !== "admin") {
    // Email verification requirement
    if (requireEmailVerification && !authStatus.emailVerified) {
      return <Navigate to="/email-verification" replace />;
    }

    // Phone verification requirement
    if (requirePhoneVerification && !authStatus.phoneVerified) {
      return <Navigate to="/phone-verification" replace />;
    }

    // Full verification requirement (both email and phone)
    if (requireFullVerification && !authStatus.canAccessDashboard) {
      if (!authStatus.emailVerified) {
        return <Navigate to="/email-verification" replace />;
      }
      if (!authStatus.phoneVerified) {
        return <Navigate to="/phone-verification" replace />;
      }
    }
  }

  return <>{children}</>;
};

// Universal Campaign Route Handler - Redirects based on user type
const CampaignRouteHandler: React.FC = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const authStatus = useAuthenticationStatus();
  const userType = localStorage.getItem("userType");

  useEffect(() => {
    // Check authentication first
    if (!authStatus.isLoggedIn) {
      const currentUrl = window.location.pathname + window.location.search;
      
      // Only store the URL if it's not a login/auth page and not already stored
      const existingRedirectUrl = localStorage.getItem('redirectAfterLogin');
      if (!existingRedirectUrl && !currentUrl.includes('/login') && !currentUrl.includes('/signup')) {
        localStorage.setItem('redirectAfterLogin', currentUrl);
      }
      
      navigate("/login", { replace: true });
      return;
    }
    
    // Redirect admins to admin interface, preserve query parameters
    if (userType === "admin") {
      const currentParams = window.location.search;
      const targetUrl = `/admin/campaigns/${campaignId}${currentParams}`;
      navigate(targetUrl, { replace: true });
    }
  }, [userType, campaignId, navigate, authStatus.isLoggedIn]);

  // Show loading while auth is being checked
  if (authStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Loading..." fullPage={true} size="large" />
      </div>
    );
  }

  // If not logged in, the useEffect will handle redirect
  if (!authStatus.isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Redirecting to login..." fullPage={true} size="large" />
      </div>
    );
  }

  // If admin, show loading while redirecting
  if (userType === "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState message="Redirecting to admin interface..." fullPage={true} size="large" />
      </div>
    );
  }

  // For lead users, render the campaign details with proper protection
  return (
    <ProtectedRoute allowedUserType="lead" requireFullVerification={true}>
      <CampaignDetails />
    </ProtectedRoute>
  );
};

// App component restructured to use hooks directly
const App: React.FC = () => {
  const authStatus = useAuthenticationStatus();
  const [userType, setUserType] = useState<string | null>(
    localStorage.getItem("userType")
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [remainingUses, setRemainingUses] = useState<number | null>(
    localStorage.getItem("remainingUses")
      ? parseInt(localStorage.getItem("remainingUses") || "0", 10)
      : null
  );
  const [showOutOfUsesModal, setShowOutOfUsesModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setUserType(localStorage.getItem("userType"));
    const storedUses = localStorage.getItem("remainingUses");
    if (storedUses) {
      setRemainingUses(parseInt(storedUses, 10));
    }
  }, [authStatus.isLoggedIn]);

  useEffect(() => {
    (window as any).updateRemainingUses = updateRemainingUses;
  }, []);

  // Check token validity and handle expiration
  useEffect(() => {
    // Only run this effect if the user is logged in
    if (!authStatus.isLoggedIn) return;

    // Function to check if token is expired
    const checkTokenValidity = () => {
      const token = localStorage.getItem("token");
      if (!token) {
        handleLogout();
        return;
      }

      // Check token's timestamp if one exists in localStorage
      const tokenTimestamp = localStorage.getItem("tokenTimestamp");
      if (tokenTimestamp) {
        const timestamp = parseInt(tokenTimestamp, 10);
        const currentTime = Date.now();
        // Check if token is older than 1 hour (3600000 milliseconds)
        if (currentTime - timestamp > 3600000) {
          console.log("Token expired - logging out");
          handleLogout();
        }
      } else {
        // If no timestamp exists, set it now
        localStorage.setItem("tokenTimestamp", Date.now().toString());
      }
    };

    // Run the check immediately
    checkTokenValidity();

    // Set up an interval to check periodically (every minute)
    const intervalId = setInterval(checkTokenValidity, 60000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [authStatus.isLoggedIn]);

  const updateRemainingUses = (uses: number | null) => {
    setRemainingUses(uses);
    if (uses !== null) {
      localStorage.setItem("remainingUses", uses.toString());
    } else {
      localStorage.removeItem("remainingUses");
    }
  };

  const handleLoginSuccess = async () => {
    setUserType(localStorage.getItem("userType"));
    const storedUses = localStorage.getItem("remainingUses");
    if (storedUses) {
      setRemainingUses(parseInt(storedUses, 10));
    }
    // Set the current timestamp when user logs in
    localStorage.setItem("tokenTimestamp", Date.now().toString());
    
    // Refresh authentication status to get latest verification info
    authStatus.refreshAuthStatus();

    // Check for stored redirect URL and navigate accordingly
    const redirectUrl = localStorage.getItem('redirectAfterLogin');
    
    if (redirectUrl) {
      localStorage.removeItem('redirectAfterLogin');
      
      // Convert URL based on user type to ensure compatibility
      const userType = localStorage.getItem("userType");
      let finalUrl = redirectUrl;
      
      if (userType === "admin" && redirectUrl.includes('/campaigns/')) {
        // Convert lead campaign URL to admin campaign URL
        finalUrl = redirectUrl.replace('/campaigns/', '/admin/campaigns/');
      } else if (userType === "lead" && redirectUrl.includes('/admin/campaigns/')) {
        // Convert admin campaign URL to lead campaign URL
        finalUrl = redirectUrl.replace('/admin/campaigns/', '/campaigns/');
      }
      
      navigate(finalUrl, { replace: true });
    } else {
      // Default behavior - check for pending agent or go to dashboard
      const pendingAgentUuid = localStorage.getItem("pendingAgentUuid");
      const pendingAgentToken = localStorage.getItem("pendingAgentToken");
      
      try {
        // If we have both an agent UUID and a token, validate the token
        if (pendingAgentUuid && pendingAgentToken) {
          const result = await validateAgentToken(pendingAgentToken);
          
          if (result.success) {
            // Clear the stored values
            localStorage.removeItem("pendingAgentUuid");
            localStorage.removeItem("pendingAgentToken");
            navigate(`/agent/${pendingAgentUuid}`, { replace: true });
          } else {
            // Token validation failed, clear and go to dashboard
            localStorage.removeItem("pendingAgentUuid");
            localStorage.removeItem("pendingAgentToken");
            navigate("/dashboard", { replace: true });
          }
        } else if (pendingAgentUuid) {
          // We have an agent UUID but no token
          localStorage.removeItem("pendingAgentUuid");
          navigate(`/agent/${pendingAgentUuid}`, { replace: true });
        } else {
          // No agent reference, go to dashboard
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("Error during redirect:", err);
        localStorage.removeItem("pendingAgentUuid");
        localStorage.removeItem("pendingAgentToken");
        navigate("/dashboard", { replace: true });
      }
    }
  };

  const handleLogout = () => {
    // Clear all authentication data
    logout();
    setUserType(null);
    setRemainingUses(null);
    localStorage.removeItem("remainingUses");
    localStorage.removeItem("tokenTimestamp");
    setIsMobileMenuOpen(false);
    
    // Refresh auth status after logout
    authStatus.refreshAuthStatus();
    
    // Navigate directly
    if (userType === "admin") {
      navigate("/admin/login", { replace: true });
    } else {
      navigate("/email-login", { replace: true });
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleConfigDropdown = () => {
    setShowConfigDropdown(!showConfigDropdown);
  };

  const closeConfigDropdown = () => {
    setShowConfigDropdown(false);
  };

  return (
    <div className="app bg-[color:var(--background)] text-[color:var(--foreground)]">
      {authStatus.isLoggedIn && (
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            scrolled
              ? "bg-knack-dark-950 shadow-lg backdrop-blur-sm"
              : "bg-knack-dark-800 backdrop-blur-[2px]"
          }`}
        >
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center h-14">
              <Link
                to="/dashboard"
                className="text-[var(--color-light)] hover:text-[var(--color-accent)] transition-colors"
                onClick={closeMobileMenu}
              >
                <h1 className="flex items-center">
                  <img
                    src="/assets/ukti-logo-with-text-white.png"
                    alt="Ukti.ai"
                    className="h-10 sm:h-12 md:h-12 w-auto object-contain max-w-none"
                  />
                </h1>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center">
                <MainNav userType={userType} />
                {/* Vertical divider */}
                <div className="h-6 w-px bg-gray-600 mx-4"></div>
                <div className="flex items-center">
                  {/* Display remaining uses for non-admin users with circular shadow */}
                  {userType !== "admin" && remainingUses !== null && (
                    <div
                      className={`mr-4 w-7 h-7 rounded-full ${
                        remainingUses > 0
                          ? "bg-[var(--color-secondary)]/10 text-[var(--color-light)] shadow-[0_0_10px_rgba(0,200,170,0.3)]"
                          : "bg-red-500/20 text-red-500 shadow-[0_0_10px_rgba(255,0,0,0.5)] animate-pulse cursor-pointer hover:bg-red-500/30"
                      } text-sm flex items-center justify-center group relative`}
                      onClick={() => {
                        if (remainingUses <= 0) {
                          setShowOutOfUsesModal(true);
                        }
                      }}
                    >
                      <span className="font-bold">{remainingUses}</span>
                      {remainingUses <= 0 && (
                        <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                          Out of uses
                        </div>
                      )}
                    </div>
                  )}
                  <ThemeToggle />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="ml-4 p-2 rounded-md bg-[var(--color-secondary)]/20 text-[var(--color-light)] hover:bg-[var(--color-secondary)] hover:text-white transition-all duration-300 flex items-center justify-center group"
                    onClick={handleLogout}
                    title="Logout"
                  >
                    <HiLogout className="h-5 w-5" />
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-in-out">
                      Logout
                    </span>
                  </motion.button>
                </div>
              </div>

              {/* Mobile Menu Button */}
              <div className="md:hidden flex items-center">
                {/* Display remaining uses on mobile with circular shadow */}
                {userType !== "admin" && remainingUses !== null && (
                  <div
                    className={`mr-3 w-6 h-6 rounded-full ${
                      remainingUses > 0
                        ? "bg-[var(--color-secondary)]/10 text-[var(--accent)] shadow-[0_0_8px_rgba(0,200,170,0.3)]"
                        : "bg-red-500/20 text-red-500 shadow-[0_0_8px_rgba(255,0,0,0.5)] animate-pulse cursor-pointer hover:bg-red-500/30"
                    } text-xs flex items-center justify-center font-bold group relative`}
                    onClick={() => {
                      if (remainingUses <= 0) {
                        setShowOutOfUsesModal(true);
                      }
                    }}
                  >
                    {remainingUses}
                    {remainingUses <= 0 && (
                      <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                        Out of uses
                      </div>
                    )}
                  </div>
                )}
                <ThemeToggle />
                <motion.button
                  className="ml-2 p-2 rounded-full w-10 h-10 flex items-center justify-center text-[var(--color-light)] hover:text-[var(--color-accent)] hover:bg-[var(--color-accent)]/10 transition-colors"
                  onClick={toggleMobileMenu}
                  whileTap={{ scale: 0.95 }}
                >
                  <AnimatePresence mode="wait">
                    {isMobileMenuOpen ? (
                      <motion.div
                        key="close"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <HiX className="h-6 w-6" />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <HiMenu className="h-6 w-6" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>

            {/* Mobile Navigation */}
            <AnimatePresence>
              {isMobileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  className="md:hidden border-t border-[color:var(--border)] py-3"
                >
                  <MobileNav
                    userType={userType}
                    closeMobileMenu={closeMobileMenu}
                  />
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="mt-3 px-4"
                  >
                    <button
                      className="w-full px-4 py-2 rounded-md bg-[color:var(--color-secondary)] hover:bg-[color:var(--color-secondary)]/90 text-white transition-colors flex items-center justify-center"
                      onClick={handleLogout}
                    >
                      <HiLogout className="h-5 w-5 mr-2" />
                      Logout
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.header>
      )}

      <main
        className={`flex-1 flex flex-col ${
          authStatus.isLoggedIn ? "pt-16 sm:pt-20 md:pt-20" : ""
        }`}
      >
        <Routes>
          {/* Public routes - redirect straight to login instead of showing landing page */}
          <Route
            path="/"
            element={
              authStatus.isLoggedIn ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/signup" replace />
              )
            }
          />

          {/* Main login route - now uses email-based authentication */}
          <Route
            path="/login"
            element={
              authStatus.isLoggedIn ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <EmailLoginWrapper onLoginSuccess={handleLoginSuccess} />
              )
            }
          />

          {/* Legacy email-login route - redirect to main login */}
          <Route
            path="/email-login"
            element={<Navigate to="/login" replace />}
          />



          <Route
            path="/email-verification"
            element={
              <EmailVerification />
            }
          />

          {/* Redirect route for backend-generated verification links */}
          <Route
            path="/auth/verify-email"
            element={<VerifyEmailRedirect />}
          />

          <Route
            path="/phone-verification"
            element={
              <PhoneVerification />
            }
          />

          <Route
            path="/verification-status"
            element={
              <VerificationStatus />
            }
          />

          <Route
            path="/email-otp-verification"
            element={
              <Navigate to="/login" replace />
            }
          />

          {/* Main signup route - uses email-based signup */}
          <Route
            path="/signup"
            element={
              authStatus.isLoggedIn ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <EmailSignupWrapper onLoginSuccess={handleLoginSuccess} />
              )
            }
          />

          {/* Legacy email-signup route - redirect to main signup */}
          <Route
            path="/email-signup"
            element={<Navigate to="/signup" replace />}
          />

          <Route
            path="/admin/login"
            element={
              authStatus.isLoggedIn ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AdminLogin onLoginSuccess={handleLoginSuccess} />
              )
            }
          />

          {/* New access route */}
          <Route 
            path="/access" 
            element={
              <div className="flex items-center justify-center min-h-screen">
                <LoadingState message="Processing access..." fullPage={true} size="large" />
              </div>
            } 
          />

          {/* Protected routes with verification requirements */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireFullVerification={true}>
                <AgentSelector />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedUserType="admin">
                <div className="min-h-screen">
                  <Routes>
                    <Route path="leads" element={<Leads />} />
                    <Route path="campaigns" element={<CampaignList />} />
                    <Route path="campaigns/:id" element={<CampaignDetails isAdmin={true} />} />
                    <Route path="agents" element={<AgentConfiguration />} />
                    <Route path="agents/:uuid" element={<AgentEdit />} />
                    <Route path="agents/new" element={<AgentCreate />} />
                    <Route path="tags" element={<TagsManagement />} />
                    <Route
                      path="language-config"
                      element={<LanguageConfiguration />}
                    />
                    <Route path="password" element={<PasswordChange />} />
                    <Route path="conversations" element={<Conversations />} />
                    <Route path="unknown" element={<UnknownConversations />} />
                    <Route
                      index
                      element={<Navigate to="/admin/agents" replace />}
                    />
                  </Routes>
                </div>
              </ProtectedRoute>
            }
          />

          <Route
            path="/agent/:uuid"
            element={
              <ProtectedRoute requireFullVerification={true}>
                <AudioAgent />
              </ProtectedRoute>
            }
          />

          {/* Conversations route for all users */}
          <Route
            path="/conversations"
            element={
              <ProtectedRoute requireFullVerification={true}>
                <Conversations />
              </ProtectedRoute>
            }
          />

          {/* Universal Campaign Route Handler - Redirects to appropriate user interface */}
          <Route
            path="/campaigns/:id"
            element={<CampaignRouteHandler />}
          />
          
          {/* Lead Campaign routes */}
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute allowedUserType="lead" requireFullVerification={true}>
                <LeadCampaigns />
              </ProtectedRoute>
            }
          />

          {/* Redirect direct agent links to login with agent parameter */}
          <Route path="/direct-agent/:uuid" element={<DirectAgentRedirect />} />

          {/* New route for file upload */}
          <Route path="/upload/:agentUuid" element={<AgentFileUpload />} />

          {/* 404 Not Found route - Must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <Footer />

      {/* Out of Uses Modal Component */}
      {showOutOfUsesModal && (
        <OutOfUsesModal
          isOpen={showOutOfUsesModal}
          onClose={() => setShowOutOfUsesModal(false)}
        />
      )}
    </div>
  );
};

// Out of Uses Modal Component
const OutOfUsesModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Out of Uses">
      <div className="p-6 text-center">
        <div className="mb-4 flex justify-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-amber-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>
        <h3 className="text-xl font-medium mb-4">You're out of uses</h3>
        <p className="text-[var(--foreground-muted)] mb-6">
          You have used all your allocated interactions with voice agents.
          Please contact{" "}
          <a
            href="mailto:sales@ukti.ai"
            className="text-[var(--accent)] font-medium"
          >
            sales@ukti.ai
          </a>{" "}
          to request more.
        </p>
        <div className="flex justify-center">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded-md font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Root component that sets up providers and router
const AppRoot: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <Router>
            <NuqsAdapter>
              <ScrollToTop />
              <App />
            </NuqsAdapter>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

// Main Navigation Component
const MainNav = ({ userType }: { userType: string | null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeConfigDropdown = () => {
    setShowConfigDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeConfigDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const NavLink = ({
    to,
    children,
  }: {
    to: string;
    children: React.ReactNode;
  }) => {
    const isActive =
      location.pathname === to ||
      (to === "/dashboard" && location.pathname === "/");

    return (
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Link
          to={to}
          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            isActive
              ? "bg-[var(--color-accent)]/20 text-[var(--color-accent)]"
              : "text-[var(--color-light)] hover:text-[var(--color-accent)]"
          }`}
        >
          {children}
        </Link>
      </motion.div>
    );
  };

  return (
    <nav className="flex items-center space-x-1">
      <NavLink to="/dashboard">Agent Portal</NavLink>
      <NavLink to="/conversations">Conversations</NavLink>
      {userType === "lead" && <NavLink to="/campaigns">Campaigns</NavLink>}
      {userType === "admin" && <NavLink to="/admin/leads">Leads</NavLink>}
      {userType === "admin" && <NavLink to="/admin/campaigns">Campaigns</NavLink>}
      {userType === "admin" && (
        <div className="relative" ref={dropdownRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowConfigDropdown(!showConfigDropdown)}
            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
              showConfigDropdown
                ? "bg-[var(--color-accent)]/20 text-[var(--color-accent)]"
                : "text-[var(--color-light)] hover:text-[var(--color-accent)]"
            }`}
          >
            Configuration
            <motion.span
              animate={{ rotate: showConfigDropdown ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="ml-1"
            >
              <HiChevronDown className="h-4 w-4" />
            </motion.span>
          </motion.button>

          <AnimatePresence>
            {showConfigDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-knack-dark-800 border border-[color:var(--accent)] py-1 z-50"
              >
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Link
                    to="/admin/agents"
                    className="block px-4 py-2 text-sm text-[var(--color-light)] hover:bg-[var(--color-accent)]/10 hover:text-[var(--color-accent)]"
                    onClick={closeConfigDropdown}
                  >
                    Agents
                  </Link>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Link
                    to="/admin/tags"
                    className="block px-4 py-2 text-sm text-[var(--color-light)] hover:bg-[var(--color-accent)]/10 hover:text-[var(--color-accent)]"
                    onClick={closeConfigDropdown}
                  >
                    Tags
                  </Link>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Link
                    to="/admin/language-config"
                    className="block px-4 py-2 text-sm text-[var(--color-light)] hover:bg-[var(--color-accent)]/10 hover:text-[var(--color-accent)]"
                    onClick={closeConfigDropdown}
                  >
                    Language Config
                  </Link>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Link
                    to="/admin/unknown"
                    className="block px-4 py-2 text-sm text-[var(--color-light)] hover:bg-[var(--color-accent)]/10 hover:text-[var(--color-accent)]"
                    onClick={closeConfigDropdown}
                  >
                    Unknown Calls
                  </Link>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </nav>
  );
};

// Mobile Navigation Component
const MobileNav = ({
  userType,
  closeMobileMenu,
}: {
  userType: string | null;
  closeMobileMenu: () => void;
}) => {
  const location = useLocation();
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);

  const NavLink = ({
    to,
    children,
  }: {
    to: string;
    children: React.ReactNode;
  }) => {
    const isActive =
      location.pathname === to ||
      (to === "/dashboard" && location.pathname === "/");
    return (
      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Link
          to={to}
          onClick={closeMobileMenu}
          className={`block px-4 py-2 text-sm font-medium transition-colors ${
            isActive
              ? "bg-[var(--color-accent)]/20 text-[var(--color-accent)]"
              : "text-[var(--color-light)] hover:bg-[var(--color-accent)]/10 hover:text-[var(--color-accent)]"
          }`}
        >
          {children}
        </Link>
      </motion.div>
    );
  };

  return (
    <div className="space-y-1">
      <NavLink to="/dashboard">Agent Portal</NavLink>
      <NavLink to="/conversations">Conversations</NavLink>

      {userType === "lead" && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <NavLink to="/campaigns">Campaigns</NavLink>
        </motion.div>
      )}

      {userType === "admin" && (
        <>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <NavLink to="/admin/leads">Leads</NavLink>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.13 }}
          >
            <NavLink to="/admin/campaigns">Campaigns</NavLink>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
          >
            <NavLink to="/admin/unknown">Unknown Calls</NavLink>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <NavLink to="/admin/agents">Agents</NavLink>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25 }}
          >
            <NavLink to="/admin/tags">Tags</NavLink>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35 }}
          >
            <NavLink to="/admin/language-config">Language Config</NavLink>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <NavLink to="/admin/password">Change Password</NavLink>
          </motion.div>
        </>
      )}
    </div>
  );
};

// Component to redirect direct agent links to login with agent parameter
const DirectAgentRedirect: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!uuid) {
      navigate("/dashboard", { replace: true });
      return;
    }

    // Store the UUID to prevent it from being lost during navigation
    localStorage.setItem("pendingAgentUuid", uuid);

    // Direct redirect without delays
    navigate(`/access?agent=${uuid}`, { replace: true });
  }, [uuid, navigate]);

  // Show loading while redirecting
  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingState
        message="Redirecting to agent..."
        fullPage={true}
        size="large"
      />
    </div>
  );
};

// Component to handle login redirection based on URL parameters
const LoginRedirect: React.FC<{ onLoginSuccess: () => void }> = ({
  onLoginSuccess,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const directAgent = searchParams.get("agent");
  const [showLoader, setShowLoader] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Save the directAgent to localStorage on mount to preserve through refresh
  useEffect(() => {
    if (directAgent) {
      localStorage.setItem("pendingAgentUuid", directAgent);
    }
  }, [directAgent]);

  const handleSuccessfulLogin = async () => {
    // Show the loader immediately
    setShowLoader(true);

    // Call the parent's loginSuccess handler first
    onLoginSuccess();

    // First check for pending agent UUID from local storage
    const pendingAgentUuid = localStorage.getItem("pendingAgentUuid");
    const pendingAgentToken = localStorage.getItem("pendingAgentToken");

    try {
      // If we have both an agent UUID and a token, we need to validate the token
      if (pendingAgentUuid && pendingAgentToken) {
        setValidatingToken(true);

        try {
          const result = await validateAgentToken(pendingAgentToken);

          if (result.success) {
            // Clear the stored values
            localStorage.removeItem("pendingAgentUuid");
            localStorage.removeItem("pendingAgentToken");

            // Redirect to the agent page
            navigate(`/agent/${pendingAgentUuid}`, { replace: true });
          } else {
            // Token validation failed
            setTokenError(
              "The access link is no longer valid. Please request a new one."
            );
            // After 3 seconds, redirect to dashboard
            setTimeout(() => {
              localStorage.removeItem("pendingAgentUuid");
              localStorage.removeItem("pendingAgentToken");
              navigate("/dashboard", { replace: true });
            }, 3000);
          }
        } catch (err) {
          setTokenError("Failed to validate access. Please try again later.");
          // After 3 seconds, redirect to dashboard
          setTimeout(() => {
            localStorage.removeItem("pendingAgentUuid");
            localStorage.removeItem("pendingAgentToken");
            navigate("/dashboard", { replace: true });
          }, 3000);
        } finally {
          setValidatingToken(false);
        }
      } else if (pendingAgentUuid) {
        // We have an agent UUID but no token
        localStorage.removeItem("pendingAgentUuid");
        navigate(`/agent/${pendingAgentUuid}`, { replace: true });
      } else if (directAgent) {
        // Direct agent parameter from URL but no stored UUID
        navigate(`/agent/${directAgent}`, { replace: true });
      } else {
        // No agent reference, go to dashboard
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Error during redirect:", err);
      navigate("/dashboard", { replace: true });
    }
  };

  // Show token error state if validation failed (in LoginRedirect)
  if (tokenError) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold mb-2">
            Access Error
          </h2>
          <p className="text-gray-600 mb-4">{tokenError}</p>
          <p className="text-xs sm:text-sm text-gray-500">
            Redirecting you to dashboard...
          </p>
          <div className="mt-4 bg-gray-100 h-1 rounded-full overflow-hidden">
            <div className="bg-blue-500 h-full animate-widthExpand"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show loader overlay if we're redirecting (in LoginRedirect)
  if (showLoader) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <LoadingState
            message={
              validatingToken
                ? "Validating your access token..."
                : "Redirecting to your agent..."
            }
            fullPage={false}
            size="large"
          />
          <p className="text-xs sm:text-sm text-gray-500 mt-4">
            {validatingToken
              ? "We're verifying your access..."
              : "Setting up your experience..."}
          </p>
        </div>
      </div>
    );
  }

  return <LeadLogin onLoginSuccess={handleSuccessfulLogin} />;
};

// Signup wrapper component for direct redirects
const SignupWrapper: React.FC<{ onLoginSuccess: () => void }> = ({
  onLoginSuccess,
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSuccessfulSignup = async () => {
    // Show the loader immediately
    setShowLoader(true);

    // Call the parent's loginSuccess handler which will handle all navigation
    onLoginSuccess();
  };

  // Show token error state if validation failed
  if (tokenError) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold mb-2">
            Access Error
          </h2>
          <p className="text-gray-600 mb-4">{tokenError}</p>
          <p className="text-xs sm:text-sm text-gray-500">
            Redirecting you to dashboard...
          </p>
          <div className="mt-4 bg-gray-100 h-1 rounded-full overflow-hidden">
            <div className="bg-blue-500 h-full animate-widthExpand"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show loader overlay if we're redirecting
  if (showLoader) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <LoadingState
            message={
              validatingToken
                ? "Validating your access token..."
                : "Redirecting to your agent..."
            }
            fullPage={false}
            size="large"
          />
          <p className="text-xs sm:text-sm text-gray-500 mt-4">
            {validatingToken
              ? "We're verifying your access..."
              : "Setting up your experience..."}
          </p>
        </div>
      </div>
    );
  }

  return <Signup onLoginSuccess={handleSuccessfulSignup} />;
};

// Component to handle email login redirection based on URL parameters
const EmailLoginWrapper: React.FC<{ onLoginSuccess: () => void }> = ({
  onLoginSuccess,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const directAgent = searchParams.get("agent");
  const [showLoader, setShowLoader] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Save the directAgent to localStorage on mount to preserve through refresh
  useEffect(() => {
    if (directAgent) {
      localStorage.setItem("pendingAgentUuid", directAgent);
    }
  }, [directAgent]);

  const handleSuccessfulEmailLogin = async () => {
    // Show the loader immediately
    setShowLoader(true);

    // Save the directAgent parameter if present
    if (directAgent) {
      localStorage.setItem("pendingAgentUuid", directAgent);
    }

    // Call the parent's loginSuccess handler which will handle all navigation
    onLoginSuccess();
  };

  // Show token error state if validation failed
  if (tokenError) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold mb-2">
            Access Error
          </h2>
          <p className="text-gray-600 mb-4">{tokenError}</p>
          <p className="text-xs sm:text-sm text-gray-500">
            Redirecting you to dashboard...
          </p>
          <div className="mt-4 bg-gray-100 h-1 rounded-full overflow-hidden">
            <div className="bg-blue-500 h-full animate-widthExpand"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show loader overlay if we're redirecting
  if (showLoader) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <LoadingState
            message={
              validatingToken
                ? "Validating your access token..."
                : "Redirecting to your agent..."
            }
            fullPage={false}
            size="large"
          />
          <p className="text-xs sm:text-sm text-gray-500 mt-4">
            {validatingToken
              ? "We're verifying your access..."
              : "Setting up your experience..."}
          </p>
        </div>
      </div>
    );
  }

  return <EmailLogin onLoginSuccess={handleSuccessfulEmailLogin} />;
};

// Component to handle email signup redirection based on URL parameters
const EmailSignupWrapper: React.FC<{ onLoginSuccess: () => void }> = ({
  onLoginSuccess,
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSuccessfulEmailSignup = async () => {
    // Show the loader immediately
    setShowLoader(true);

    // Call the parent's loginSuccess handler which will handle all navigation
    onLoginSuccess();
  };

  // Show token error state if validation failed
  if (tokenError) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold mb-2">
            Access Error
          </h2>
          <p className="text-gray-600 mb-4">{tokenError}</p>
          <p className="text-xs sm:text-sm text-gray-500">
            Redirecting you to dashboard...
          </p>
          <div className="mt-4 bg-gray-100 h-1 rounded-full overflow-hidden">
            <div className="bg-blue-500 h-full animate-widthExpand"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show loader overlay if we're redirecting
  if (showLoader) {
    return (
      <div className="absolute inset-0 z-50 bg-[color:var(--background)] flex flex-col items-center justify-center px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <LoadingState
            message={
              validatingToken
                ? "Validating your access token..."
                : "Redirecting to your agent..."
            }
            fullPage={false}
            size="large"
          />
          <p className="text-xs sm:text-sm text-gray-500 mt-4">
            {validatingToken
              ? "We're verifying your access..."
              : "Setting up your experience..."}
          </p>
        </div>
      </div>
    );
  }

  return <EmailSignup onSignupSuccess={handleSuccessfulEmailSignup} />;
};

// Removed unused wrapper components

// Agent Access component to handle /access?agent= route
const AgentAccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const agentUuid = searchParams.get("agent");
  const token = searchParams.get("token");
  const [loading, setLoading] = useState<boolean>(true);
  const [validatingToken, setValidatingToken] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleAccess = async () => {
      if (!agentUuid) {
        // If no agent UUID, redirect to dashboard immediately
        navigate("/", { replace: true });
        return;
      }

      // Store the agent UUID in localStorage for persistence through refresh
      localStorage.setItem("pendingAgentUuid", agentUuid);

      // If token is provided, also store it
      if (token) {
        localStorage.setItem("pendingAgentToken", token);
      }

      try {
        // Check if user is logged in
        if (isAuthenticated()) {
          if (token) {
            // User is logged in and token is provided, validate token
            setValidatingToken(true);
            try {
              const result = await validateAgentToken(token);

              if (result.success) {
                // Token is valid, redirect to the agent page
                navigate(`/agent/${agentUuid}`, { replace: true });
              } else {
                // Token validation failed
                setError(
                  "The access link is no longer valid. Please request a new one."
                );
                // After 3 seconds, redirect to home
                setTimeout(() => {
                  navigate("/", { replace: true });
                }, 3000);
              }
            } catch (err) {
              setError("Failed to validate access. Please try again later.");
              // After 3 seconds, redirect to home
              setTimeout(() => {
                navigate("/", { replace: true });
              }, 3000);
            } finally {
              setValidatingToken(false);
            }
          } else {
            // Already logged in but no token, redirect directly to the agent
            navigate(`/agent/${agentUuid}`, { replace: true });
          }
        } else {
          // Not logged in, redirect to signup with the agent UUID and token preserved in localStorage
          navigate("/signup", { replace: true });
        }
      } catch (err) {
        setError("An unexpected error occurred. Please try again later.");
        setLoading(false);
      }
    };

    handleAccess();
  }, [agentUuid, token, navigate]);

  // Show error state if there's an error
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 md:px-8">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold mb-2">
            Access Error
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-xs sm:text-sm text-gray-500">
            Redirecting you shortly...
          </p>
          <div className="mt-4 bg-gray-100 h-1 rounded-full overflow-hidden">
            <div className="bg-blue-500 h-full animate-widthExpand"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while redirecting or validating
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 md:px-8">
      <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md max-w-md w-full text-center">
        <LoadingState
          message={
            validatingToken
              ? "Validating your access..."
              : "Preparing your agent experience..."
          }
          fullPage={false}
          size="large"
        />
        <p className="text-xs sm:text-sm text-gray-500 mt-4">
          {validatingToken
            ? "We're verifying your access token..."
            : "Setting up your agent..."}
        </p>
      </div>
    </div>
  );
};

// Component to redirect from /auth/verify-email to /email-verification 
const VerifyEmailRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      // Redirect to the main email verification route with the token
      navigate(`/email-verification?token=${token}`, { replace: true });
    } else {
      // No token, redirect to email verification without token
      navigate('/email-verification', { replace: true });
    }
  }, [searchParams, navigate]);

  // Show loading while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)]">
      <motion.div
        className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="flex flex-col items-center">
          <motion.div
            className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-blue-600"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </motion.div>
          <h2 className="text-xl font-bold text-[color:var(--foreground)] text-center mb-2">
            Processing Your Link
          </h2>
          <p className="text-[color:var(--muted-foreground)] text-center">
            Please wait while we verify your email address...
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AppRoot;
